import { ApolloError } from '@apollo/client';
import {
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { Autocomplete } from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';
import { JSX } from 'react';
import { ClientType } from '../../types';

export interface ClientSearchProps {
  sortedClients: ClientType[];
  clientInsurersLoading: boolean;
  handleSearch: (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => void;
  setClientId: (clientId: string) => void;
  isClientDisabled: boolean;
  titleColor?: string;
  compactMode?: boolean;
  clientInsurersError?: ApolloError;
}

export const ClientSearch = ({
  sortedClients,
  clientInsurersLoading,
  clientInsurersError,
  handleSearch,
  setClientId,
  isClientDisabled,
  titleColor,
  compactMode,
}: ClientSearchProps): JSX.Element => (
  <HHStack
    border="1px solid lightgray"
    borderRadius={1}
    spacing={2}
    padding={4}
    sx={(theme): SystemStyleObject<Theme> => ({
      backgroundColor: theme.palette.background.default,
    })}
  >
    <HHTypography hhVariant="body" color={titleColor ?? ''}>
      Clients
    </HHTypography>
    <form onSubmit={handleSearch}>
      <HHStack direction="row" spacing={2}>
        <Autocomplete
          freeSolo
          options={clientInsurersError ? [] : sortedClients}
          loading={clientInsurersLoading}
          getOptionLabel={(option: ClientType | string): string =>
            typeof option === 'string'
              ? option
              : `${option.name} (${option.id})`
          }
          isOptionEqualToValue={(
            option: ClientType,
            value: ClientType,
          ): boolean => option.id === value.id || option.name === value.name}
          filterOptions={(options, params): ClientType[] =>
            options.filter(
              option =>
                option.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase()) ||
                option.id.toString().includes(params.inputValue),
            )
          }
          size="small"
          onInputChange={(_, newInputValue): void => {
            if (!Number.isNaN(Number(newInputValue))) {
              setClientId(newInputValue);
            }
          }}
          onChange={(_, newValue: ClientType | string | null): void => {
            if (newValue && typeof newValue === 'object') {
              setClientId(newValue?.id.toString() ?? '');
            }
          }}
          sx={{ minWidth: 150 }}
          fullWidth
          renderInput={(params): JSX.Element => (
            <HHTextField
              {...params}
              hhVariant="variant-bypass"
              variant="outlined"
              placeholder={'Search by Name or ID'}
              error={!!clientInsurersError}
              helperText={
                clientInsurersError ? 'Error fetching clients' : undefined
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <SearchOutlinedIcon
                    sx={{
                      color: 'action.active',
                    }}
                  />
                ),
              }}
            />
          )}
        />
        {!compactMode && (
          <HHButton
            hhVariant="contained"
            aria-label="submit"
            type="submit"
            disabled={isClientDisabled}
            onClick={handleSearch}
          >
            Search
          </HHButton>
        )}
      </HHStack>
    </form>
  </HHStack>
);
